import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import Bluebird from 'bluebird';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Link,
  Tooltip,
  IconButton,
  Box,
  Button,
  TableFooter,
  Typography,
  TextField,
  Autocomplete,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Check, Close, Cancel, FileCopy as FileCopyIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { omit } from 'lodash';
import { confirmAlert } from 'react-confirm-alert';
import Linkify from 'react-linkify';
import DOMPurify from 'dompurify';
import ClearChip from './ClearChip';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import routes from '../routes';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import TableActionButton from './TableActionButton';
import TableActionsButtonsWrapper from './TableActionsButtonsWrapper';
import {
  donorLinkFilters,
  getRescueAdopter,
  getRescuePickupLocationFullName,
  getRescueRescuer,
  isHTML,
  isRescueClaimed,
  renderPickupLocationNameOrAdress,
} from '../helpers/RescuesHelper';
import { formatTime } from '../helpers/formatters';
import { generateCustomCsvFileName } from '../helpers/csvUtilities';
import * as authService from '../services/auth';
import * as sitesActions from '../actions/sites';
import { getMuiTableDataIndex, getReceiversWithSiteOptions } from '../helpers/getters';
import { sortAlphabetically, sortByDateAndPickupWindowStartAndEndTime, sortByPickupReady, sortByTime } from '../helpers/sorters';
import RescuesTableStatusBox, { useRescuesTableRowColorStyles } from './Common/RescuesTableStatusBox';
import { RescueFilterTypes } from '../containers/FutureRescuesListView';
import { Roles } from '../models/roles';
import ConfirmationDialog from './ConfirmationDialog';
import UndoButton from './UndoButton';
import { fetchReceiversIfNeeded } from '../actions/receivers';
import { Colors } from '../assets/theme/Colors';
import useSystemSettings from '../hooks/useSystemSettings';
import { LBS_PER_MEAL } from '../models/systemSettings';
import DonorLinkPickupIndicator from './DonorLinkPickupIndicator';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from './FeatureFlagWrapper';
import useActiveSite from '../hooks/useActiveSite';
import { daysFilter, foodDonorNameFilterOptions } from '../helpers/MuiTableCustomFilters';
import { fetchSiteGroupsIfNeeded } from '../actions/groups';

const customTableToolbar = ({
  handleEditClick,
  editMode,
  showEditRescueButton = true,
  hasActiveBulkEditFeatureFlag = true,
  handleReviewClick,
  reviewButtonEnabled,
}) => (
  <>
    {!editMode && showEditRescueButton && hasActiveBulkEditFeatureFlag && (
      <Tooltip title="Edit Mode">
        <IconButton onClick={handleEditClick} size="large">
          <BorderColorIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {editMode && (
      <Box display="flex" justifyContent="flex-end" flexWrap="no-wrap">
        <Button onClick={handleEditClick}>Exit Edit Mode</Button>
        <Button
          disabled={!reviewButtonEnabled}
          onClick={handleReviewClick}
          style={{ marginLeft: '1rem' }}
          variant="contained"
          color="primary"
        >
          Review and Save
        </Button>
      </Box>
    )}
  </>
);

const customEditFooter = ({ handleEditClick, handleReviewClick, reviewButtonEnabled }) => (
  <TableFooter>
    <TableRow>
      <TableCell
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'nowrap',
          marginRight: '24px',
          minHeight: '64px',
        }}
      >
        <Typography variant="body2" style={{ marginRight: '1rem' }}>
          Exit Edit Mode to select a different page
        </Typography>
        <Button onClick={handleEditClick}>Exit Edit Mode</Button>
        <Button
          disabled={!reviewButtonEnabled}
          onClick={handleReviewClick}
          style={{ marginLeft: '1rem' }}
          variant="contained"
          color="primary"
        >
          Review and Save
        </Button>
      </TableCell>
    </TableRow>
  </TableFooter>
);

const RescuesTable = ({
  tableId,
  sites = [],
  rescues,
  isLoading,
  renderNamesAsLinks,
  showSiteColumn,
  handleReviewClick,
  editedRescues,
  setEditedRescues,
  editMode,
  setEditMode,
  showActionsColumn = true,
  showCopyRescueButton = true,
  showEditRescueButton = true,
  removedFilters = [],
  disableCancelAfterPickupDate = false,
  dateRange = null,
  umbrellaFoodDonorName,
}) => {
  const history = useHistory();
  const activeSite = useActiveSite();
  const tableStyles = useRescuesTableRowColorStyles();
  const dispatch = useDispatch();
  const modifyRescue = (id, attrs) => dispatch(sitesActions.modifySiteRescue(id, attrs));
  const saveRescue = id => dispatch(sitesActions.saveSiteRescue(id));
  const resetRescue = id => dispatch(sitesActions.resetSiteRescue(id));
  const user = authService.getCurrentlyLoggedInOrImpersonatingUser();
  const receiversEntities = useSelector(state => state.entities.receivers);
  const receiversList = getReceiversWithSiteOptions(Object.values(receiversEntities.byId).sort((a, b) =>
    sortAlphabetically('asc', b.name, a.name)
  ));
  const rescueSizes = useSelector(state => state.entities.rescueSizes.byId);
  const groups = useSelector(state => state.entities.groups);
  const siteGroupIds = groups.bySiteId[activeSite.id] || [];
  const groupsList = siteGroupIds.map(id => groups.byId[id]).filter(Boolean);
  const availableGroups = groupsList.map((group) => group.name);
  const lbsPerMeal = useSystemSettings(LBS_PER_MEAL);
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);
  const isAdmin = authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);

  const toolPaletteSx = {
    height: 'auto',
    padding: '2px 2px 2px 2px',
  };
  const toolPaletteCellSx = {
    padding: '2px 2px 2px 2px',
    border: 'none',
    display: 'flex',
  };

  useEffect(() => {
    dispatch(fetchReceiversIfNeeded());
  }, []);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchSiteGroupsIfNeeded(activeSite.id));
    }
  }, [isAdmin]);

  const handleEditClick = () => {
    if (!showEditRescueButton) {
      setEditMode(false);
      return setEditedRescues([]);
    }

    if (editedRescues?.length > 0) {
      return confirmAlert({
        message: 'Are you sure you want to exit without saving?',
        buttons: [
          {
            label: 'No',
            color: 'primary',
          },
          {
            label: 'Yes',
            color: 'primary',
            onClick: () => {
              setEditMode(!editMode);
              setEditedRescues([]);
            },
          },
        ],
        customUI: ({ message, onClose, buttons }) => (
          <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
        ),
      });
    }

    setEditMode(!editMode);
    return setEditedRescues([]);
  };

  const onEditableFieldChange = (rescueEdited, rescue, data) => {
    if (!rescueEdited) {
      return setEditedRescues([...editedRescues, { rescue_id: rescue.id, ...data }]);
    }

    return setEditedRescues(editedRescues.map((r) => (r.rescue_id !== rescue.id ? r : { ...rescueEdited, ...data })));
  };

  const handleUndoClick = (rescue, field, previousWeight = 0) => {
    const editedRescuesReduced = editedRescues.reduce((acc, curr) => {
      if (curr.rescue_id === rescue.rescue_id) {
        let reducedRescue = omit(curr, [field]);

        if (field === 'receiving_agency_id') {
          reducedRescue = omit(reducedRescue, ['receiver']);
        }

        if (Object.keys(reducedRescue).length === 1) {
          return acc;
        }

        acc.push(reducedRescue);

        return acc;
      }
      acc.push(curr);
      return acc;
    }, []);

    setEditedRescues(editedRescuesReduced);
  };

  const onRescueCancel = rescue => modifyRescue(rescue.id, { cancelled_by_id: user.id, canceller: user.firstname });

  const onRescueUnCancel = rescue => modifyRescue(rescue.id, { cancelled_by_id: null, canceller: null });

  const onReleaserClear = rescue => modifyRescue(rescue.id, { released_by_id: null, releaser: null });

  const onEdit = (rescue) => history.push(generatePath(routes.rescue_edit, { rescueId: rescue.id }), { prevScrollPosition: window.scrollY });

  const onCopy = async rescue => {
    // If user has admin or SD roles go to donation create
    if (
      authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
      ])
    ) {
      return history.push(generatePath(routes.donation_add_new), { copy: true, rescue: rescue });
    }

    // If user has Food Donor roles go to create donation request
    if (
      authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
        Roles.DonorLocationAdmin,
        Roles.DonorLocationStaff,
      ])
    ) {
      return history.push(generatePath(routes.donation_create_request), { copy: true, rescue: rescue });
    }

    return null;
  };

  const onReset = rescue => resetRescue(rescue.id);

  const onSave = rescue =>
    Bluebird.try(() => saveRescue(rescue.id))
      .then(() => snackbarHelper.success('Rescue updated.'))
      .catch(err => errorsHelper.handleError(err));

  const renderUserProfileLink = user => {
    if (!user) {
      return '-';
    }

    return (
      <Link
        onClick={() => history.push(generatePath(routes.userEditNew, { id: user.id }))}
        style={{ cursor: 'pointer' }}
        underline="hover"
      >
        {user.name}
      </Link>
    );
  };

  const dateCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];
    const formattedDate = moment(value, 'YYYYMMDD').format('MM/DD/YYYY');

    if (csv) {
      return formattedDate;
    }

    return (
      <>
        <span>{formattedDate}</span>

        <RescuesTableStatusBox rescue={rescue} />
      </>
    );
  };

  const dayCustomBodyRender = (value, tableMeta) =>
    moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd');

  const pickupBeginCustomBodyRender = value => formatTime(value);

  const pickupEndCustomBodyRender = value => formatTime(value);

  const locationCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return false;
    }

    const rescuePickupLocationFullName = getRescuePickupLocationFullName(rescue) || '';

    if (csv || !renderNamesAsLinks) {
      return rescuePickupLocationFullName;
    }

    return (
      <Link
        onClick={() =>
          history.push(
            generatePath(routes.foodDonor, {
              foodDonorId: rescue.location_id,
            })
          )
        }
        style={{ cursor: 'pointer' }}
        underline="hover"
        // value set for search to work
        value={rescuePickupLocationFullName}
      >
        {rescuePickupLocationFullName}
      </Link>
    );
  };

  const receiverCustomBodyRender = (value, tableMeta) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];
    if (!rescue) {
      return false;
    }

    const siteReceivers = receiversList.filter((receiver) => receiver.site_id === rescue.site_id);

    if (editMode) {
      const rescueEdited = editedRescues.find((r) => r.rescue_id === rescue.id);
      return (
        <Box display="flex">
          <Autocomplete
            sx={
              rescueEdited?.receiving_agency_id
                ? {
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: `3px solid ${Colors.bulkEdit.main}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: `2px solid ${Colors.bulkEdit.main}`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: `3px solid ${Colors.bulkEdit.main}`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: Colors.bulkEdit.main,
                    },
                  }
                : { backgroundColor: 'white' }
            }
            style={{ width: 250 }}
            value={{
              id: rescueEdited?.receiving_agency_id || rescue.receiver_id,
              name: rescueEdited?.receiving_agency_id ? rescueEdited?.receiver : rescue.receiver,
              site_id: rescue.site_id,
            }}
            fullWidth
            size="small"
            loading={receiversEntities.inflight}
            name="receiver_id"
            placeholder="Select receiver"
            options={siteReceivers}
            disableClearable
            onChange={(event, value) => {
              onEditableFieldChange(rescueEdited, rescue, { receiving_agency_id: value.id, receiver: value.name });
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => !option.id}
            renderOption={(props, option) => (
              <li {...props}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="body1">{option.name}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select receiver" variant="outlined" />}
          />
          {rescueEdited?.receiving_agency_id && (
            <UndoButton handleClick={() => handleUndoClick(rescueEdited, 'receiving_agency_id')} />
          )}
        </Box>
      );
    }

    if (!renderNamesAsLinks) {
      return value || '';
    }

    return (
      <Link
        onClick={() => history.push(generatePath(routes.receiver, { id: rescue.receiver_id }))}
        style={{ cursor: 'pointer' }}
        underline="hover"
      >
        {value || ''}
      </Link>
    );
  };

  const pickupLocationCustomBodyRender = (value, tableMeta) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return false;
    }

    const rescuePickupLocationFullName = renderPickupLocationNameOrAdress(rescue) || '';

    return rescuePickupLocationFullName;
  };

  const adopterCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return false;
    }

    const rescueAdopter = getRescueAdopter(rescue);

    if (csv || !renderNamesAsLinks) {
      return rescueAdopter ? rescueAdopter.name : null;
    }

    return renderUserProfileLink(rescueAdopter);
  };

  const rescuerCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return false;
    }

    const rescueRescuer = getRescueRescuer(rescue);

    if (csv || !renderNamesAsLinks) {
      return rescueRescuer ? rescueRescuer.name : null;
    }

    return renderUserProfileLink(rescueRescuer);
  };

  const groupsCustomBodyRender = (value) => {
    if (!value) {
      return '-';
    }

    let parsed;

    try {
      parsed = JSON.parse(value);
    } catch (e) {
      parsed = value;
    }

    if (Array.isArray(parsed)) {
      const groupNames = parsed.map((group) => group.name);

      if (groupNames.length) {
        return groupNames.join(', ');
      }

      return '-';
    }

    return '-';
  };

  const claimerCustomBodyRender = (value) => value ? 'Claimed' : 'Unclaimed';

  const weightCustomBodyRender = (value, tableMeta) => {
    if (value) {
      return value;
    }

    const rescue = rescues[getMuiTableDataIndex(tableMeta)];
    const rescueSize = Object.values(rescueSizes).find((size) => size.id === rescue.rescue_size_id);
    const rescueSizeLbs = rescueSize?.meals_per_rescue ? rescueSize.meals_per_rescue * lbsPerMeal : null;

    return rescueSizeLbs || '-';
  };

  const cancellerCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue || !rescue.canceller) {
      return !csv ? '-' : null;
    }

    if (csv || !renderNamesAsLinks) {
      return rescue.canceller;
    }

    return <ClearChip value={rescue.canceller} onClick={() => onRescueUnCancel(rescue)} />;
  };

  const releaserCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue || !rescue.releaser) {
      return !csv ? '-' : null;
    }

    if (csv || !renderNamesAsLinks) {
      return rescue.releaser;
    }

    return <ClearChip value={rescue.releaser} onClick={() => onReleaserClear(rescue)} />;
  };

  const vehicleCustomBodyRenderer = (value) => {
    const rescueSize = Object.values(rescueSizes).find((size) => size.id === value);

    return rescueSize?.name || '-';
  };

  const pickupReadyCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    if (!rescue?.location_donor_link_active) {
      return '-';
    }

    if (csv) {
      return value ? 'Pickup Ready' : 'No food yet';
    }

    return <DonorLinkPickupIndicator pickupReady={!!value} />;
  };

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        filter: false,
        sort: false,
        display: false,
      },
    },
  ];

  if (showSiteColumn) {
    columns.push({
      name: 'site_id',
      label: 'Site',
      options: {
        customBodyRender: value => {
          if (!sites[value]) {
            return 'n/a';
          }

          return sites[value].name;
        },
        customBodyRenderCSV: value => {
          if (!sites[value]) {
            return 'n/a';
          }

          return sites[value].name;
        },
        filter: false,
      },
    });
  }

  columns.push(
    {
      name: 'date',
      label: 'Date',
      options: {
        sort: true,
        filter: false,
        sortCompare:
          (order) =>
          ({ rowData: row1 }, { rowData: row2 }) =>
            sortByDateAndPickupWindowStartAndEndTime({
              order,
              firstDate: row1[1],
              firstBegin: row1[3],
              firstEnd: row1[4],
              secondDate: row2[1],
              secondBegin: row2[3],
              secondEnd: row2[4],
            }),
        customBodyRender: (value, tableMeta) => dateCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => dateCustomBodyRender(value, tableMeta, true),
      },
    },
    {
      name: 'day',
      label: 'Day',
      options: {
        filter: true,
        customBodyRender: (value, tableMeta) => dayCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => dayCustomBodyRender(value, tableMeta),
        customFilterListOptions: {
          render: value => `Day: ${value}`,
        },
        ...daysFilter,
      },
    },
    {
      name: 'pickup_begin',
      label: 'Pickup Begin',
      options: {
        filter: false,
        customBodyRender: value => pickupBeginCustomBodyRender(value),
        customBodyRenderCSV: value => pickupBeginCustomBodyRender(value),
        sortCompare: order => (row1, row2) => sortByTime(order, row1.data, row2.data),
      },
    },
    {
      name: 'pickup_end',
      label: 'Pickup End',
      options: {
        filter: false,
        customBodyRender: value => pickupEndCustomBodyRender(value),
        customBodyRenderCSV: value => pickupEndCustomBodyRender(value),
        sortCompare: order => (row1, row2) => sortByTime(order, row1.data, row2.data),
      },
    },
    {
      name: 'location',
      label: 'Food Donor',
      options: {
        customBodyRender: (value, tableMeta) => locationCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => locationCustomBodyRender(value, tableMeta, true),
        customFilterListOptions: {
          render: value => `Food Donor: ${value}`,
        },
        filter: !removedFilters.includes('location'),
        // links set "value" to the full name, keep the filter on the plain food donor name
        ...(renderNamesAsLinks
          ? { filterOptions: foodDonorNameFilterOptions(rescues, getRescuePickupLocationFullName) }
          : {}),
      },
    },
    {
      name: 'pickup_location_name',
      label: 'Pickup Location',
      options: {
        customBodyRender: (value, tableMeta) => pickupLocationCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => pickupLocationCustomBodyRender(value, tableMeta),
        customFilterListOptions: {
          render: value => `Pickup Location ${value}`,
        },
        filter: false,
        display: false,
      },
    },
    {
      name: 'receiver',
      label: 'Receiving Agency',
      options: {
        forceDisplay: !!editMode,
        customBodyRender: (value, tableMeta) => receiverCustomBodyRender(value, tableMeta),
        customFilterListOptions: {
          render: value => `Receiving Agency: ${value}`,
        },
      },
    },
    {
      name: 'pickup_city',
      label: 'Pickup City',
      options: {
        forceDisplay: !!editMode,
        customBodyRender: (value) => value || '',
        customFilterListOptions: {
          render: value => `Pickup City: ${value}`,
        },
      },
    },
    {
      name: 'dropoff_city',
      label: 'Dropoff City',
      options: {
        forceDisplay: !!editMode,
        customBodyRender: (value) => value || '',
        customFilterListOptions: {
          render: value => `Dropoff City: ${value}`,
        },
      },
    },
    {
      name: 'rescue_size_id',
      label: 'Vehicle',
      options: {
        display: false,
        sort: true,
        sortCompare: order => (row1, row2) => {
          const orderMultiplier = order === 'asc' ? 1 : -1;

          const row1RescueSize = Object.values(rescueSizes).find((size) => size.id === row1.data);
          const row2RescueSize = Object.values(rescueSizes).find((size) => size.id === row2.data);
          if (row1RescueSize && row2RescueSize) {
            return (row1RescueSize.meals_per_rescue - row2RescueSize.meals_per_rescue) * orderMultiplier;
          }

          return row1.data - row2.data;
        },
        customBodyRender: value => vehicleCustomBodyRenderer(value),
        customBodyRenderCSV: value => vehicleCustomBodyRenderer(value),
      },
    },
    {
      name: 'slug',
      label: 'Description',
      options: {
        filter: false,
        customBodyRender: value => value || '-',
      },
    },
    {
      name: 'pickup_notes',
      label: 'Pickup Notes',
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => {
          if (!value) {
            return '-';
          }

          if (isHTML(value)) {
            return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
          }

          return (
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a target="blank" href={decoratedHref} key={key}>
                  {decoratedText}
                </a>
              )}
            >
              {value}
            </Linkify>
          );
        },
      },
    },
    {
      name: 'adopter',
      label: 'Adopter',
      options: {
        customFilterListOptions: {
          render: value => `Adopted by: ${value === '-' ? 'unadopted' : value}`,
        },
        customBodyRender: (value, tableMeta) => adopterCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => adopterCustomBodyRender(value, tableMeta, true),
      },
    },
    {
      name: 'rescuer',
      label: 'Rescuer',
      options: {
        filter: true,
        customBodyRender: (value, tableMeta) => rescuerCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => rescuerCustomBodyRender(value, tableMeta, true),
        customFilterListOptions: {
          render: value => `Rescuer: ${value}`,
        },
      },
    },
    ...(hasActiveDonorLinkFF
      ? [
          {
            name: 'rescue_confirmed',
            label: 'Donor Link',
            options: {
              display: true,
              filter: true,
              customBodyRender: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta),
              customBodyRenderCSV: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta, true),
              filterOptions: {
                names: [
                  donorLinkFilters.dataReceived,
                  donorLinkFilters.dataNotReceived,
                  donorLinkFilters.donorLinkInactive,
                  donorLinkFilters.donorLinkActive,
                ],
                logic: (option, filters, row) => {
                  if (!filters || filters.length === 0) {
                    return false;
                  }

                  const rescueId = row[0];
                  const rescue = rescues.find((r) => r.id === rescueId);

                  switch (filters[0]) {
                    case donorLinkFilters.dataReceived:
                      return !rescue.rescue_confirmed;
                    case donorLinkFilters.dataNotReceived:
                      return !!option;
                    case donorLinkFilters.donorLinkInactive:
                      return rescue.location_donor_link_active;
                    case donorLinkFilters.donorLinkActive:
                      return !rescue.location_donor_link_active;
                    default:
                      return true;
                  }
                },
              },
              customFilterListOptions: {
                render: (value) => `Donor Link: ${value}`,
              },
              sortCompare: (order) => (row1, row2) => {
                const rescue1 = rescues.find((fd) => fd.id === row1.rowData[0]);
                const rescue2 = rescues.find((fd) => fd.id === row2.rowData[0]);

                return sortByPickupReady(order, rescue1, rescue2);
              },
            },
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            name: 'donation_groups',
            label: 'Group(s)',
            options: {
              filter: true,
              customFilterListOptions: {
                render: (value) => `Group: ${value.replace('_', '')}`,
              },
              customBodyRender: (value) => groupsCustomBodyRender(value),
              customBodyRenderCSV: (value) => groupsCustomBodyRender(value),
              sortFilterList: false,
              filterOptions: {
                // Add underscore prefix to display as a first option (filter options are sorted internally)
                names: ['_None', ...availableGroups],
                renderValue: (val) => val.replace('_', ''),
                logic(value, filterVal) {
                  if (!filterVal || filterVal.length === 0) {
                    return false;
                  }

                  if (filterVal.includes('_None')) {
                    return value !== '-';
                  }
                  return !filterVal.some((group) => value.includes(group));
                },
              },
            },
          },
        ]
      : []),
    {
      name: 'claimer',
      label: 'Rescue Status',
      options: {
        filter: true,
        customFilterListOptions: {
          render: value => `Rescue Status: ${value}`,
        },
        customBodyRender: value => claimerCustomBodyRender(value),
        customBodyRenderCSV: value => claimerCustomBodyRender(value),
        filterOptions: {
          names: [RescueFilterTypes.claimed, RescueFilterTypes.unclaimed],
          logic(value, filterVal) {
            if (filterVal.includes(RescueFilterTypes.claimed)) {
              return value !== 'Claimed';
            }

            if (filterVal.includes(RescueFilterTypes.unclaimed)) {
              return value !== 'Unclaimed';
            }

            return false;
          },
        },
      },
    },
    {
      name: 'lbs',
      label: 'Rescue Weight',
      options: {
        filter: true,
        customFilterListOptions: {
          render: value => `Weight: ${value}`,
        },
        customBodyRender: (value, tableMeta) => weightCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => weightCustomBodyRender(value, tableMeta),
      },
    },
    {
      name: 'canceller',
      label: 'Cancelled By',
      options: {
        empty: true,
        filter: false,
        searchable: false,
        customBodyRender: (value, tableMeta) => cancellerCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => cancellerCustomBodyRender(value, tableMeta, true),
      },
    },
    {
      name: 'releaser',
      label: 'Released By',
      options: {
        empty: true,
        filter: false,
        searchable: false,
        customBodyRender: (value, tableMeta) => releaserCustomBodyRender(value, tableMeta),
        customBodyRenderCSV: (value, tableMeta) => releaserCustomBodyRender(value, tableMeta, true),
      },
    }
  );

  if (showActionsColumn) {
    columns.push({
      name: 'actions',
      label: 'Actions',
      options: {
        download: false,
        Empty: true,
        filter: false,
        searchable: false,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => {
          const rescue = rescues[getMuiTableDataIndex(tableMeta)];

          if (!rescue) {
            return false;
          }

          const now = moment();
          const pickupTime = moment(`${rescue?.date} ${rescue?.pickup_begin}`, 'YYYY-MM-DD HH:mm:ss');
          const currentTimeIsAfterPickupBegins = now.isAfter(pickupTime);

          if (rescue.modified) {
            return (
              <Table sx={toolPaletteSx}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={toolPaletteCellSx}>
                      <TableActionsButtonsWrapper>
                        <TableActionButton title="Reset" icon={Close} onClick={() => onReset(rescue)} />

                        <TableActionButton title="Save" icon={Check} onClick={() => onSave(rescue)} />
                      </TableActionsButtonsWrapper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            );
          }

          return (
            <Table sx={toolPaletteSx}>
              <TableBody>
                <TableRow>
                  <TableCell sx={toolPaletteCellSx}>
                    <TableActionsButtonsWrapper>
                      {!rescue.cancelled_by_id && (
                        <TableActionButton
                          disabled={disableCancelAfterPickupDate && currentTimeIsAfterPickupBegins}
                          title={
                            disableCancelAfterPickupDate && currentTimeIsAfterPickupBegins
                              ? `This rescue is already in progress, if you need to cancel please contact ${activeSite?.primary_contact_firstname} ${activeSite?.primary_contact_lastname}`
                              : 'Cancel Rescue'
                          }
                          icon={Cancel}
                          onClick={() => {
                            if (rescue.rescue_confirmed) {
                              return confirmAlert({
                                message: `The donor has already logged items for  donation, are you sure you want to  cancel? If so, make sure you contact  the donor and let them know you won't be picking up!`,
                                buttons: [
                                  {
                                    label: 'Back',
                                    color: 'secondary',
                                  },
                                  {
                                    label: 'Cancel Rescue',
                                    color: 'primary',
                                    onClick: () => onRescueCancel(rescue),
                                  },
                                ],
                                customUI: ({ message, onClose, buttons }) => (
                                  <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
                                ),
                              });
                            }
                            
                            return onRescueCancel(rescue)}
                          }
                        />
                      )}
                      {showEditRescueButton && (
                        <TableActionButton title="Edit Rescue" onClick={() => onEdit(rescue)} />
                      )}
                      {showCopyRescueButton && (
                        <TableActionButton
                          icon={FileCopyIcon}
                          title="Copy Rescue"
                          onClick={() => onCopy(rescue)}
                        />
                      )}
                    </TableActionsButtonsWrapper>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          );
        },
      },
    });
  }

  return (
    <ThemeProvider
      theme={theme =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MUIDataTableHeadCell: {
              styleOverrides: {
                root: {
                  ...theme.components.MUIDataTableHeadCell.styleOverrides.root,
                  '&:last-child': {
                    width: 130,
                  },
                },
              },
            },
          },
        })
      }
    >
      <BaseMUIDataTable
        isLoading={isLoading}
        tableId={tableId}
        data={rescues}
        columns={columns}
        options={{
          customToolbar: () =>
            customTableToolbar({
              handleEditClick,
              editMode,
              showEditRescueButton,
              handleReviewClick,
              reviewButtonEnabled: editedRescues?.length,
            }),
          customFooter: editMode
            ? () => customEditFooter({ handleEditClick, handleReviewClick, reviewButtonEnabled: editedRescues.length })
            : null,
          sortOrder: {
            name: 'date',
            direction: 'asc',
          },
          setRowProps: (row, dataIndex) => {
            const rescue = rescues[dataIndex];

            let colorClass = tableStyles.unclaimedRescueRow;
            const rescueEdited = editedRescues.find((r) => r.rescue_id === rescue.id);

            if (rescue.cancelled_by_id) {
              colorClass = tableStyles.cancelledRescueRow;
            } else if (rescue.closed_by_id) {
              colorClass = tableStyles.closedRescueRow;
            } else if (isRescueClaimed(rescue)) {
              colorClass = tableStyles.claimedRescueRow;
            } else if (!isRescueClaimed(rescue)) {
              colorClass = tableStyles.unclaimedRescueRow;
            }

            return {
              className: rescueEdited ? `editedRow ${colorClass}` : colorClass,
            };
          },
          rowsPerPage: 15,
          selectableRows: 'none',
          responsive: 'simple',
          download: !editMode,
          filter: !editMode,
          search: !editMode,
          viewColumns: !editMode,
          downloadOptions: {
            filename: umbrellaFoodDonorName 
              ? `${umbrellaFoodDonorName} Umbrella Food Donor Future Rescues.csv`
              : generateCustomCsvFileName({
                  baseFileName: activeSite?.name ? `${activeSite.name} Upcoming Rescues` : 'Upcoming Rescues',
                  dateRange: dateRange ? {
                    start: dateRange.from,
                    end: dateRange.to
                  } : null
                }),
          },
        }}
      />
    </ThemeProvider>
  );
};

export default RescuesTable;
